import { useEffect, useState, useCallback, useRef } from "react";
import { OperatorProvider, useOperator } from "@/context/OperatorContext";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import api, { formatApiError } from "@/lib/api";
import CompleteWorkOrderDialog from "@/components/CompleteWorkOrderDialog";
import { Button } from "@/components/ui/button";
import { Wrench, PlayCircle, LogOut, Loader2, Bot, CheckCircle2, Bike } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { scheduled: "Scheduled service", battery: "Battery", repair: "Repair", manual: "Manual" };

function MechanicCard({ wo, onChange }) {
  const start = async () => {
    try { await api.post(`/operator/work-orders/${wo.id}/start`); toast.success("Work started"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`mech-wo-${wo.id}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-lg font-semibold">{wo.vehicle_reg}</p>
          <p className="text-sm text-muted-foreground">{wo.description}</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
          {wo.source === "auto" ? <Bot className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
          {TYPE_LABEL[wo.type] || wo.type}
        </span>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">Odometer at open: {wo.odometer_at_open ?? "—"} km</div>
      <div className="mt-4 flex items-center gap-2">
        {wo.status === "open" && (
          <Button data-testid={`mech-start-${wo.id}`} className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90" onClick={start}>
            <PlayCircle className="h-4 w-4" /> Start work
          </Button>
        )}
        {wo.status === "in_progress" && (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-status-rented/15 px-2.5 py-1 text-xs font-medium text-status-rented">In progress</span>
            <CompleteWorkOrderDialog workOrder={wo} onDone={onChange} />
          </>
        )}
      </div>
    </div>
  );
}

function MechanicShell() {
  const { ctx } = useOperator();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [denied, setDenied] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/operator/work-orders");
      setOrders(data); setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 8000);
    return () => clearInterval(timer.current);
  }, [load]);

  const active = (orders || []).filter((o) => o.status !== "done");
  const done = (orders || []).filter((o) => o.status === "done");

  return (
    <div className="min-h-screen bg-tenant-bg">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-tenant-border bg-tenant-surface/90 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-tenant-button bg-tenant-primary text-tenant-primary-foreground">
            <Wrench className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">{ctx?.branding?.logo_text || "Workshop"}</p>
            <p className="text-xs text-muted-foreground">Mechanic · {user?.name}</p>
          </div>
        </div>
        <Button data-testid="mech-logout-btn" variant="ghost" size="sm" onClick={logout} className="gap-1.5">
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 p-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My work orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">Vehicles assigned to you for service. Complete one to send it back on the road.</p>
        </div>

        {orders === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : denied ? (
          <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center text-sm text-muted-foreground">
            The maintenance module is not enabled for this operator.
          </div>
        ) : active.length === 0 ? (
          <div data-testid="mech-empty" className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
            <Bike className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No open work assigned. You're all caught up.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {active.map((wo) => <MechanicCard key={wo.id} wo={wo} onChange={load} />)}
          </div>
        )}

        {done.length > 0 && (
          <section>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-status-available" /> Recently completed
            </h2>
            <div className="space-y-2">
              {done.slice(0, 8).map((wo) => (
                <div key={wo.id} data-testid={`mech-done-${wo.id}`} className="flex items-center justify-between rounded-tenant-card border border-tenant-border bg-tenant-surface px-4 py-3 text-sm">
                  <span className="font-medium">{wo.vehicle_reg}</span>
                  <span className="text-muted-foreground">₹{wo.total_cost} · {wo.description}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default function MechanicApp() {
  useDocumentTitle("Workshop");
  return (
    <OperatorProvider>
      <MechanicShell />
    </OperatorProvider>
  );
}
